"use client";

import { useTranslations } from "next-intl";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { SupportedCurrency } from "@/lib/utils/currency";
import {
  isOneWayReturnSelection,
  type OneWayReturnOption,
} from "@/lib/mobility/one-way-return";

interface OneWayReturnNoticeProps {
  oneWay: OneWayReturnOption;
  pickupBranchId: string;
  returnBranchId: string;
  sourceCurrency: SupportedCurrency;
}

/**
 * 반납 지점 셀렉트 아래 편도 반납 안내(opt-in 상품만, 09-22).
 * 수령 ≠ 반납일 때만 주황 박스(같으면 아무것도 없음) —
 * 요금은 예약 결제에 포함되지 않으며 예약 확정 후 파트너가 수량(km)을 안내하면
 * 같은 옵션을 단독 구매한다. 가격은 옵션 선택지의 고객가(마크업 적용, 상세 조회가 이미 적용).
 */
export default function OneWayReturnNotice({
  oneWay,
  pickupBranchId,
  returnBranchId,
  sourceCurrency,
}: OneWayReturnNoticeProps) {
  const t = useTranslations("product.booking.oneWayReturn");
  const { formatFromSource } = useCurrency();
  // "1km당 ¥195" — 단위는 선택지 라벨(파트너가 "1km"·"10km"·"1회" 등으로 정의)
  const price = t("perUnit", {
    price: formatFromSource(oneWay.choice.price, sourceCurrency),
    unit: oneWay.choice.label,
  });

  // 수령 = 반납이면 아무것도 그리지 않는다(09-22 결정) — 편도일 때만 안내.
  if (!isOneWayReturnSelection(pickupBranchId, returnBranchId)) return null;
  return (
    <div className="mt-2 rounded-[10px] border border-orange-200 bg-orange-50 px-3 py-2.5">
      <p className="break-keep text-[13px] font-semibold leading-snug text-orange-700">
        {t("title", { price })}
      </p>
      <p className="mt-0.5 break-keep text-xs leading-relaxed text-gray-600">
        {t("body")}
      </p>
    </div>
  );
}
